import {
  CanonicalDataType,
  MessagePathNode,
  MessagePathPartType,
} from "@/shared/domain/topics";

import {
  GroupedPlotSeries,
  PlotPanelState,
  PlotSeries,
  PlotSeriesMember,
  TopicData,
} from "./schema";
import { PlotSeriesConfig } from "./schema/v1";

/**
 * Whether two TopicData entries reference the same renderable signal.
 *
 * The server-assigned messagePath.id alone is not a sufficient identity: an
 * array field has a single message-path record, so every index of the array
 * (e.g. `axes[1]` vs `axes[2]`) shares one id and differs only in its slice
 * parts. Parts are compared via their string form, which also normalizes a
 * full-array slice's `end` across its persisted (`null`) and in-memory
 * (`Infinity`) representations.
 */
export function isSameDataSource(a: TopicData, b: TopicData): boolean {
  return (
    a.messagePath.id === b.messagePath.id &&
    MessagePathNode.partsToString(a.messagePath.parts) ===
      MessagePathNode.partsToString(b.messagePath.parts)
  );
}

export function idForClip(): string {
  return crypto.randomUUID();
}

export function idForSeries(): string {
  return crypto.randomUUID();
}

export function idForMap(data: TopicData[]): string {
  if (data.length > 0) {
    // A map path's data is a latitude/longitude pair for one file. Build the
    // id from the server-assigned message-path ids (unique per file) so two
    // paths sharing a topic name across different files get distinct ids.
    return data
      .map((d) => d.messagePath.id)
      .sort()
      .join("-");
  } else {
    return "Map";
  }
}

export function isGroupedSeries(
  series: PlotSeries,
): series is GroupedPlotSeries {
  return "kind" in series && series.kind === "group";
}

/**
 * The config an entry carries itself: a single series' own, or nothing for a
 * group, whose per-line settings live on its members.
 */
export function seriesConfig(series: PlotSeries): PlotSeriesConfig | undefined {
  return isGroupedSeries(series) ? undefined : series.config;
}

/**
 * The panel entry `id` names, or the group whose member it names. Member ids
 * are unique across the panel, so the per-series actions (set config, set
 * visibility, remove) resolve either kind of id through this one lookup.
 */
export function findSeriesOrMember(
  panel: PlotPanelState,
  id: string,
): { entry: PlotSeries; member?: PlotSeriesMember } | undefined {
  for (const entry of panel.data) {
    if (entry.id === id) {
      return { entry };
    }
    if (isGroupedSeries(entry)) {
      const member = entry.members.find((m) => m.id === id);
      if (member !== undefined) {
        return { entry, member };
      }
    }
  }
  return undefined;
}

/**
 * The elements `data`'s path groups, when it groups any. That is a path
 * ending in an `Array` or `NumberArray` attribute followed by a slice that is
 * the only slice in the path, and that slice is either the whole array
 * (`axes[:]`, the slice the topic tree appends; the result has no `end`) or a range
 * of more than one element (`axes[3:6]`, by absolute index, `end`
 * exclusive). A persisted open-ended slice carries `end: null`.
 *
 * Anything else groups nothing: an indexed slice (`axes[3]`) is one element,
 * and an open slice that starts past 0 (`axes[2:]`) is a single series with
 * its elements flattened into one line. An
 * array nested in another array (`section[:].tension[:]`) is not grouped
 * either: value extraction flattens across the outer slice, so the elements
 * observed belong to every outer item at once.
 */
export function groupSlice(
  data: TopicData,
): { start: number; end?: number } | undefined {
  const parts = data.messagePath.parts;
  const slice = parts.at(-1);
  const attr = parts.at(-2);
  if (
    slice?.type !== MessagePathPartType.Slice ||
    attr?.type !== MessagePathPartType.Attr ||
    (attr.dataType !== CanonicalDataType.Array &&
      attr.dataType !== CanonicalDataType.NumberArray) ||
    parts.filter((part) => part.type === MessagePathPartType.Slice).length !== 1
  ) {
    return undefined;
  }
  const end = slice.end ?? Infinity;
  if (end === Infinity) {
    return slice.start === 0 ? { start: 0 } : undefined;
  }
  return end - slice.start > 1 ? { start: slice.start, end } : undefined;
}
